import { hasAnyTranslation } from '@assets/i18n/config';
import {
  type QueryFetchLogsQuery,
  SubStepTypes,
  type User,
  type WorkflowCompletion,
} from '@generated/graphql';
import { LanguageCodes } from '@onevesthq/ov-enums';
import { enumValues } from '@utils/enumValues';
import { isEqual } from 'lodash-es';
import { z } from 'zod/v3';
import type {
  changesSchema,
  DisplayTextForValue,
  LabelsByKey,
} from './workflowReview.schemas';
import {
  ChangeValueSchema,
  changeObjectSchema,
  createTranslation,
  isChangePair,
  parseTranslation,
  type translationSchema,
} from './workflowReview.schemas';
import {
  camelCaseToTitleCase,
  getKnownFieldLabel,
} from './workflowReviewKnownFields';

const fieldsToIgnore = [
  'createdAt',
  'completedAt',
  'updatedAt',
  'suitabilityScore',
  'oldSuitabilityScore',
  '_',
  'complianceIssueSource',
  'isAccreditedInvestor',
];

// Substep types that save to a field with a different name than their option keys.
// These need special handling to map the field name to the substep's title.
const substepFieldMappings: Partial<Record<SubStepTypes, keyof User>> = {
  [SubStepTypes.RiskQuestion_1]: 'riskQuestion1',
  [SubStepTypes.RiskQuestion_2]: 'riskQuestion2',
  [SubStepTypes.InvestmentKnowledge]: 'investmentKnowledge',
};

const customFieldItemSchema = z.object({
  key: z.string(),
  value: z.unknown(),
});

const customFieldsChangePairSchema = z.tuple([
  z.array(customFieldItemSchema),
  z.array(customFieldItemSchema),
]);

type CustomFieldItem = z.infer<typeof customFieldItemSchema>;
type ExpandCustomFieldsResult =
  | { status: 'expanded'; entries: [string, [string, string]][] }
  | { status: 'unchanged' }
  | { status: 'invalid' };

function expandCustomFields(
  changePair: [unknown, unknown],
): ExpandCustomFieldsResult {
  const parsedChangePair = customFieldsChangePairSchema.safeParse(changePair);

  // If the shape is unexpected, keep default parsing behavior downstream.
  if (!parsedChangePair.success) {
    return { status: 'invalid' };
  }

  const [oldArr, newArr] = parsedChangePair.data;
  const toMap = (arr: CustomFieldItem[]) =>
    new Map(arr.map((item) => [item.key, item]));
  const oldByKey = toMap(oldArr);
  const newByKey = toMap(newArr);
  const stringify = (value: unknown): string =>
    value == null
      ? ''
      : typeof value === 'object'
        ? JSON.stringify(value)
        : String(value);

  const allKeys = Array.from(
    new Set(Array.from(oldByKey.keys()).concat(Array.from(newByKey.keys()))),
  );
  const changedEntries = allKeys
    .filter(
      (cfKey) =>
        !isEqual(oldByKey.get(cfKey)?.value, newByKey.get(cfKey)?.value),
    )
    .map((cfKey): [string, [string, string]] => {
      const oldValue = oldByKey.get(cfKey)?.value;
      const newValue = newByKey.get(cfKey)?.value;
      return [cfKey, [stringify(oldValue), stringify(newValue)]];
    });

  if (changedEntries.length === 0) {
    return { status: 'unchanged' };
  }

  return { status: 'expanded', entries: changedEntries };
}

export function computeChanges(
  translateBackendString: (translatedObject: unknown) => string,
  logs: QueryFetchLogsQuery['fetchLogs']['logs'] | undefined,
  workflowCompletion: WorkflowCompletion,
  language: LanguageCodes = LanguageCodes.EN,
): z.infer<typeof changesSchema> {
  // Build a step name map from workflow.steps and currentStep
  const stepNameMap = [
    ...(workflowCompletion.workflow.steps || []),
    // Add currentStep last so it can override (might be more up-to-date when navigating)
    workflowCompletion.currentStep,
  ].reduce((map, step: any) => {
    if (step?.id && step?.name) {
      const parsedName = parseTranslation(step.name);
      if (parsedName && hasAnyTranslation(parsedName)) {
        map.set(step.id, parsedName);
      }
    }
    return map;
  }, new Map<string, z.infer<typeof translationSchema>>());

  const stepChangesByStepId = (logs || []).reduce(
    (acc, log) => {
      const stepId = log.workflowContext?.stepId;
      if (!stepId) return acc;

      if (!(stepId in acc)) {
        // Get step name from the map, or fall back to stepId
        const stepNameFromMap = stepNameMap.get(stepId);
        const stepName = stepNameFromMap
          ? stepNameFromMap
          : createTranslation(stepId);

        acc[stepId] = {
          stepName,
          changes: {},
        };
      }

      const { data: changeObject } = changeObjectSchema.safeParse(log.changes);

      if (!changeObject) {
        console.warn('Invalid change object in log:', log);
        return acc;
      }

      // Find the step to get substep information
      const step =
        workflowCompletion.workflow.steps.find(
          (step: any) => step.id === stepId,
        ) ??
        (workflowCompletion.currentStep?.id === stepId
          ? workflowCompletion.currentStep
          : undefined);

      const subStepId = log.workflowContext?.subStepId;
      const relevantSubStep = step?.subSteps?.find(
        (s: any) => s.id === subStepId,
      );

      const labels: LabelsByKey = Object.entries(
        relevantSubStep?.options || {},
      ).reduce((acc: LabelsByKey, [optKey, optValue]: [string, any]) => {
        if (!optValue || typeof optValue !== 'object') return acc;

        // Handle substeps that save to a field with a different name than their option keys
        const fieldName =
          substepFieldMappings[relevantSubStep?.type as SubStepTypes];
        if (fieldName && relevantSubStep?.options?.title) {
          const titleLabel = parseTranslation(
            relevantSubStep?.options?.title,
            createTranslation(fieldName),
          );
          acc[fieldName] = {
            label: titleLabel,
            subStep: { id: relevantSubStep?.id },
          };
        }

        const rawLabel = optValue.label ?? optValue.title;
        if (!rawLabel) return acc;

        const normalizedLabel: z.infer<typeof translationSchema> =
          typeof rawLabel === 'string'
            ? createTranslation(rawLabel)
            : parseTranslation(rawLabel);

        acc[optKey] = {
          label: normalizedLabel,
          subStep: { id: relevantSubStep?.id },
        };

        return acc;
      }, {} as LabelsByKey);

      const ignoredKeys = Object.entries(relevantSubStep?.options || {}).reduce(
        (acc: Set<string>, [optKey, optValue]: [string, any]) => {
          if (!optValue || typeof optValue !== 'object') return acc;

          const rawLabel = optValue.label ?? optValue.title;
          if (!rawLabel) return acc;

          const isUnderscore =
            typeof rawLabel === 'string'
              ? rawLabel.trim() === '_'
              : enumValues(LanguageCodes).every(
                  (lang) => rawLabel[lang] === '_',
                );

          if (isUnderscore) {
            acc.add(optKey);
          }
          return acc;
        },
        new Set<string>(),
      );

      const options =
        relevantSubStep?.options?.options &&
        Array.isArray(relevantSubStep?.options?.options)
          ? relevantSubStep?.options?.options
          : [];

      const displayTextForValue = options?.reduce(
        (acc: DisplayTextForValue, option: any) => {
          acc[option?.value] = translateBackendString(option?.label);
          return acc;
        },
        {} as DisplayTextForValue,
      );

      Object.entries(changeObject)
        .filter(([key]) => !fieldsToIgnore.includes(key))
        .filter(([key]) => !ignoredKeys.has(key))
        .flatMap(([key, value]): [string, unknown][] => {
          if (key !== 'customFields') {
            return [[key, value]];
          }

          const changePair: [unknown, unknown] = isChangePair(value)
            ? value
            : [log.originalDocument?.[key], log.newDocument?.[key]];
          const customFieldsResult = expandCustomFields(changePair);

          if (customFieldsResult.status === 'unchanged') {
            return [];
          }
          if (customFieldsResult.status === 'invalid') {
            return [[key, value]];
          }
          return customFieldsResult.entries;
        })
        .forEach(([key, value]) => {
          const schema = ChangeValueSchema(displayTextForValue, key, language);
          const changePair: [unknown, unknown] = isChangePair(value)
            ? value
            : [log.originalDocument?.[key], log.newDocument?.[key]];

          const { data: parsedChangepair } = z
            .tuple([schema, schema])
            .safeParse(changePair);
          if (!parsedChangepair) {
            console.error(
              `could not parsed change pair for key ${key} of value: ${JSON.stringify(changePair)}`,
            );
            return;
          }

          const [oldValue, newValue] = parsedChangepair;

          const labelMeta = labels[key];
          const fieldLabel: z.infer<typeof translationSchema> = labelMeta
            ? labelMeta.label
            : (getKnownFieldLabel(key) ??
              createTranslation(camelCaseToTitleCase(key)));

          const finalSubStep = labelMeta?.subStep ?? {
            id: log?.workflowContext?.subStepId,
          };

          const stepChanges = acc[stepId];
          if (stepChanges) {
            if (!stepChanges.changes[key]) {
              stepChanges.changes[key] = {
                fields: [],
                originalAndLastUpdated: {
                  key: fieldLabel,
                  oldValue: '',
                  newValue: '',
                  subStep: finalSubStep,
                },
              };
            }

            stepChanges.changes[key].fields.push({
              key: fieldLabel,
              oldValue,
              newValue,
              subStep: finalSubStep,
            });
          }
        });

      return acc;
    },
    {} as z.infer<typeof changesSchema>,
  );

  Object.values(stepChangesByStepId).forEach((stepChanges) => {
    if (!stepChanges) return;

    Object.values(stepChanges.changes).forEach((fieldChanges) => {
      if (fieldChanges && fieldChanges.fields.length > 0) {
        fieldChanges.originalAndLastUpdated.newValue =
          fieldChanges.fields[0].newValue;
        fieldChanges.originalAndLastUpdated.oldValue =
          fieldChanges.fields[fieldChanges.fields.length - 1].oldValue;
      }
    });
  });

  return stepChangesByStepId;
}
